import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Calculator, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  ArrowRight,
  Sparkles,
  DollarSign
} from 'lucide-react';

const ROICalculator: React.FC = () => {
  const { language } = useLanguage();
  
  // State for user inputs
  const [currency, setCurrency] = useState<'MXN' | 'USD'>('MXN');
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(350000);
  const [employees, setEmployees] = useState<number>(45);
  const [adminHours, setAdminHours] = useState<number>(25);

  // Constants for calculation
  const HOURLY_RATE = currency === 'MXN' ? 180 : 10; // Average labor rate per hour
  const MONTHLY_LAIMU_COST = currency === 'MXN' ? 12000 : 650; // Estimated subscription + service amortized

  // Calculations
  // 1. Time savings: 65% of administrative hours saved by automations (Carta Porte, CFDI, HR surveys)
  const hoursSavedPerWeek = Math.round(adminHours * 0.65);
  const weeklySavingsVal = hoursSavedPerWeek * HOURLY_RATE * (employees > 10 ? Math.min(Math.floor(employees * 0.15), 8) : 1);
  const annualTimeSavings = weeklySavingsVal * 52;

  // 2. Revenue recovery (leakage prevention, billing compliance, sales conversions)
  // Estimated 2.5% recovery of monthly revenue annually (30% of revenue * 2.5% saved)
  const annualRevenueRecovery = monthlyRevenue * 12 * 0.022;

  // 3. Total Annual Value
  const totalAnnualSavings = Math.round(annualTimeSavings + annualRevenueRecovery);

  // 4. Annual Investment in Laimu
  const annualInvestment = MONTHLY_LAIMU_COST * 12;

  // 5. ROI Multiplier
  const roiMultiplier = (totalAnnualSavings / Math.max(annualInvestment, 1)).toFixed(1);

  // Formatter helper
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'es-MX', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 via-white to-orange-50/30">
      <div className="container mx-auto px-4 md:px-6">
        
        {/* Title Block */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#fff7ed', color: '#ff8c00' }}>
            <Calculator className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Interactive ROI Simulator' : 'Simulador de Retorno de Inversión'}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            {language === 'en' ? 'Calculate Your ROI with LAIMU' : 'Calcula tu Retorno de Inversión con LAIMU'}
          </h2>
          <p className="text-lg text-gray-600">
            {language === 'en' 
              ? 'See how much time and money you can save by automating operations and unifying your systems with Laimu.'
              : 'Descubre cuánto tiempo y dinero puedes ahorrar al automatizar tus operaciones y unificar tus sistemas con Laimu.'
            }
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch font-sans">
          
          {/* Sliders Input Panel (Col span 7) */}
          <div className="lg:col-span-7 bg-white p-6 md:p-8 rounded-3xl shadow-xl border border-gray-150 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-950">
                  {language === 'en' ? 'Your Business Profile' : 'Perfil de tu Empresa'}
                </h3>
                {/* Currency selector toggle */}
                <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                  <button
                    onClick={() => setCurrency('MXN')}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                      currency === 'MXN' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    MXN ($)
                  </button>
                  <button
                    onClick={() => setCurrency('USD')}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                      currency === 'USD' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    USD ($)
                  </button>
                </div>
              </div>

              {/* Slider 1: Monthly Revenue */}
              <div className="mb-8">
                <div className="flex justify-between text-sm mb-3">
                  <span className="font-medium text-gray-700">
                    {language === 'en' ? 'Monthly Revenue' : 'Ingresos Mensuales'}
                  </span>
                  <span className="font-bold text-lg" style={{ color: '#115d7b' }}>
                    {formatCurrency(monthlyRevenue)}
                  </span>
                </div>
                <input
                  type="range"
                  min={currency === 'MXN' ? 50000 : 3000}
                  max={currency === 'MXN' ? 5000000 : 250000}
                  step={currency === 'MXN' ? 25000 : 1000}
                  value={monthlyRevenue}
                  onChange={(e) => setMonthlyRevenue(Number(e.target.value))}
                  className="w-full h-2 rounded-lg cursor-pointer accent-[#115d7b] bg-gray-200"
                  style={{ accentColor: '#115d7b' }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                  <span>{formatCurrency(currency === 'MXN' ? 50000 : 3000)}</span>
                  <span>{formatCurrency(currency === 'MXN' ? 5000000 : 250000)}</span>
                </div>
              </div>

              {/* Slider 2: Number of Employees */}
              <div className="mb-8">
                <div className="flex justify-between text-sm mb-3">
                  <span className="font-medium text-gray-700">
                    {language === 'en' ? 'Number of Employees' : 'Número de Empleados'}
                  </span>
                  <span className="font-bold text-lg" style={{ color: '#115d7b' }}>
                    {employees} {language === 'en' ? 'employees' : 'empleados'}
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="350"
                  step="5"
                  value={employees}
                  onChange={(e) => setEmployees(Number(e.target.value))}
                  className="w-full h-2 rounded-lg cursor-pointer accent-[#115d7b] bg-gray-200"
                  style={{ accentColor: '#115d7b' }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                  <span>5 {language === 'en' ? 'emp' : 'emp'}</span>
                  <span>350+ {language === 'en' ? 'emp' : 'emp'}</span>
                </div>
              </div>

              {/* Slider 3: Weekly Administrative Hours */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-3">
                  <span className="font-medium text-gray-700">
                    {language === 'en' ? 'Administrative Hours / Week' : 'Horas de Administración / Semanal'}
                  </span>
                  <span className="font-bold text-lg" style={{ color: '#115d7b' }}>
                    {adminHours} hrs
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="80"
                  step="1"
                  value={adminHours}
                  onChange={(e) => setAdminHours(Number(e.target.value))}
                  className="w-full h-2 rounded-lg cursor-pointer accent-[#115d7b] bg-gray-200"
                  style={{ accentColor: '#115d7b' }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                  <span>5 hrs {language === 'en' ? 'weekly' : 'semanales'}</span>
                  <span>80 hrs {language === 'en' ? 'weekly' : 'semanales'}</span>
                </div>
              </div>
            </div>

            {/* Note about averages */}
            <div className="bg-gray-50 border border-gray-150 rounded-xl p-3.5 flex items-start gap-2.5 text-xs text-gray-500 mt-4">
              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <p>
                {language === 'en'
                  ? 'Calculations are based on average operation improvements: 65% reduction in administrative times, 90% error reduction in SAT/SCT billing, and 2.2% annual revenue recovery via optimized CRM pipelines.'
                  : 'Los cálculos están basados en mejoras promedio observadas: 65% de reducción en tiempos administrativos, 90% de reducción de errores de facturación SAT/SCT, y 2.2% de recuperación anual de ingresos por optimización de pipeline CRM.'
                }
              </p>
            </div>
          </div>

          {/* Savings Results Panel (Col span 5) */}
          <div className="lg:col-span-5 text-white rounded-3xl shadow-xl p-6 md:p-8 flex flex-col justify-between relative overflow-hidden" style={{ backgroundColor: '#115d7b' }}>
            {/* Background design elements */}
            <div className="absolute top-0 right-0 w-36 h-36 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-400/10 rounded-full blur-3xl -ml-20 -mb-20"></div>

            <div className="relative z-10">
              <h3 className="text-lg font-bold text-blue-200 uppercase tracking-wider mb-6">
                {language === 'en' ? 'Estimated Annual Impact' : 'Impacto Anual Estimado'}
              </h3>

              {/* Big Savings Metric */}
              <div className="mb-8">
                <span className="text-sm text-blue-100 block mb-1">
                  {language === 'en' ? 'Estimated Annual Value' : 'Ahorro e Impacto Anual Estimado'}
                </span>
                <span className="text-4xl md:text-5xl font-extrabold tracking-tight">
                  {formatCurrency(totalAnnualSavings)}
                </span>
              </div>

              {/* Sub metrics grid */}
              <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-6 mb-6">
                <div>
                  <span className="text-[11px] text-blue-200 block mb-1">
                    {language === 'en' ? 'Admin Time Saved' : 'Tiempo Liberado'}
                  </span>
                  <span className="text-xl font-bold flex items-center gap-1.5 text-green-300">
                    <TrendingUp className="w-4 h-4" />
                    ~65%
                  </span>
                </div>
                <div>
                  <span className="text-[11px] text-blue-200 block mb-1">
                    {language === 'en' ? 'ROI Multiplier' : 'Retorno de Inversión'}
                  </span>
                  <span className="text-xl font-bold text-orange-300 flex items-center gap-1">
                    <Sparkles className="w-4.5 h-4.5" />
                    {roiMultiplier}x
                  </span>
                </div>
              </div>

              {/* ROI explanation text */}
              <p className="text-sm text-blue-100/90 leading-relaxed mb-6">
                {language === 'en' 
                  ? `For every $1 invested in LAIMU Trantor, you are projected to return ${roiMultiplier} times its cost in operational efficiency and leakage recovery.`
                  : `Por cada $1 invertido en LAIMU Trantor, se proyecta un retorno de ${roiMultiplier} veces su costo en eficiencia operativa y recuperación de cartera.`
                }
              </p>
            </div>

            {/* CTA Button */}
            <div className="relative z-10">
              <a
                href="/demo"
                className="w-full px-5 py-4 text-gray-900 rounded-xl transition-all duration-300 font-bold text-center flex items-center justify-center gap-2 group hover:scale-102"
                style={{ 
                  backgroundColor: '#ff8c00', 
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(255, 140, 0, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e67e00';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 140, 0, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ff8c00';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 140, 0, 0.4)';
                }}
              >
                <span>
                  {language === 'en' ? 'Get Detailed Custom Audit' : 'Solicitar Auditoría de ROI Completa'}
                </span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default ROICalculator;
